import { isMatch, isString, merge, pick } from 'lodash';
import qs from 'qs';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import usePersistent from 'hooks/use-persistent';
import { ROUTES } from 'pages/Search/constants';
import { formatVersion, noneIf, parseVersion } from 'pages/Search/utils';

import { PRODUCT_CVE_LIST_SIZE } from '../constants';

interface Payload {
    vendor: string;
    product: string;
    page: number;
    version?: string;
    year?: number;
    month?: number;
    score?: number;
}

const defaults: Payload = {
    vendor: '',
    product: '',
    page: 1,
    version: undefined,
    year: undefined,
    month: undefined,
    score: undefined
};

export function useProductCveCatalogPayload() {
    const history = useHistory();
    const location = useLocation();
    const parameters = useParams<{ vendor: string; product: string; version: string; page: string }>();
    const query = useMemo(() => qs.parse(location.search, { ignoreQueryPrefix: true }), [location.search]);

    const page = parameters.page ? parseInt(parameters.page) : defaults.page;
    const vendor = parameters.vendor ? decodeURIComponent(parameters.vendor) : defaults.vendor;
    const product = parameters.product ? decodeURIComponent(parameters.product) : defaults.product;
    const version = parameters.version ? parseVersion(parameters.version) : defaults.version;

    const year = query.year ? parseInt(query.year.toString()) : defaults.year;
    const month = query.month ? parseInt(query.month.toString()) : defaults.month;
    const score = query.score ? parseInt(query.score.toString()) : defaults.score;

    const payload = useMemo(
        () => ({ page, vendor, product, version, year, month, score, size: PRODUCT_CVE_LIST_SIZE }),
        [month, page, product, score, vendor, version, year]
    );
    const payloadRef = usePersistent(payload);

    const url = useCallback(
        (patch: Partial<Payload>) => {
            const previous = payloadRef.current;
            const current = merge({}, previous, patch);

            const path = ROUTES.PRODUCT_CVES({
                vendor: current.vendor,
                product: current.product,
                year: current.year,
                month: current.year ? current.month : undefined,
                score: current.score,
                version: isString(current.version) ? formatVersion(current.version) : undefined,
                page: isMatch(
                    previous,
                    pick(current, ['vendor', 'product', 'version', 'year', 'month', 'score', 'size'])
                )
                    ? noneIf(current.page, defaults.page)
                    : null
            });

            return path;
        },
        [payloadRef]
    );

    const set = useCallback((patch: Partial<Payload>) => history.push(url(patch)), [history, url]);

    const actions = useMemo(() => ({ url, set }), [set, url]);

    return [payload, actions] as const;
}
